import { createSlice, createSelector } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

// Export states
export const EXPORT_STATUS = {
  PENDING: 'pending',
  EXPORTING: 'exporting',
  COMPLETED: 'completed',
  FAILED: 'failed'
};

const initialState = {
  exports: {}, // Object with export_id as key
  isExportPanelOpen: false,
  totalActiveExports: 0
};

const exportSlice = createSlice({
  name: 'export',
  initialState,
  reducers: {
    // Initialize a new export task
    initializeExport: (state, action) => {
      const { exportName, exportId: providedExportId, exportType = 'default' } = action.payload;
      const exportId = providedExportId || uuidv4();
      
      state.exports[exportId] = {
        export_id: exportId,
        export_name: exportName,
        export_type: exportType,
        status: EXPORT_STATUS.PENDING,
        file_url: null,
        file_name: null,
        error: null,
        startTime: Date.now(),
        endTime: null,
        retryCount: 0
      };
      
      state.totalActiveExports += 1;
      state.isExportPanelOpen = true;
    },

    // Start exporting
    startExport: (state, action) => {
      const { exportId } = action.payload;
      if (state.exports[exportId]) {
        state.exports[exportId].status = EXPORT_STATUS.EXPORTING;
      }
    },

    // Mark export as completed
    completeExport: (state, action) => {
      const { exportId, file_url, file_name } = action.payload;
      if (state.exports[exportId]) {
        state.exports[exportId].status = EXPORT_STATUS.COMPLETED;
        state.exports[exportId].file_url = file_url;
        state.exports[exportId].file_name = file_name;
        state.exports[exportId].endTime = Date.now();
        state.totalActiveExports = Math.max(0, state.totalActiveExports - 1);
      }
    },

    // Mark export as failed
    failExport: (state, action) => {
      const { exportId, error } = action.payload;
      if (state.exports[exportId]) {
        state.exports[exportId].status = EXPORT_STATUS.FAILED;
        state.exports[exportId].error = error;
        state.exports[exportId].endTime = Date.now();
        state.totalActiveExports = Math.max(0, state.totalActiveExports - 1);
      }
    },

    // Retry failed export
    retryExport: (state, action) => {
      const { exportId } = action.payload;
      if (state.exports[exportId]) {
        state.exports[exportId].status = EXPORT_STATUS.PENDING;
        state.exports[exportId].error = null;
        state.exports[exportId].retryCount += 1;
        state.exports[exportId].file_url = null;
        state.exports[exportId].file_name = null;
        state.totalActiveExports += 1;
      }
    },

    // Remove export from list (user manually closes)
    removeExport: (state, action) => {
      const { exportId } = action.payload;
      if (state.exports[exportId]) {
        const wasActive = [EXPORT_STATUS.PENDING, EXPORT_STATUS.EXPORTING].includes(
          state.exports[exportId].status
        );
        if (wasActive) {
          state.totalActiveExports = Math.max(0, state.totalActiveExports - 1);
        }
        delete state.exports[exportId];
      }
    },

    // Toggle export panel visibility
    toggleExportPanel: (state) => {
      state.isExportPanelOpen = !state.isExportPanelOpen;
    },

    // Close export panel
    closeExportPanel: (state) => {
      state.isExportPanelOpen = false;
    },

    // Clear all completed exports
    clearCompletedExports: (state) => {
      Object.keys(state.exports).forEach(exportId => {
        if (state.exports[exportId].status === EXPORT_STATUS.COMPLETED) {
          delete state.exports[exportId];
        }
      });
    },

    // Clear all exports (for cleanup)
    clearAllExports: (state) => {
      state.exports = {};
      state.totalActiveExports = 0;
      state.isExportPanelOpen = false;
    }
  }
});

export const {
  initializeExport,
  startExport,
  completeExport,
  failExport,
  retryExport,
  removeExport,
  toggleExportPanel,
  closeExportPanel,
  clearCompletedExports,
  clearAllExports
} = exportSlice.actions;

// Selectors
export const selectAllExports = (state) => state.export.exports;
export const selectExportById = (exportId) => (state) => state.export.exports[exportId];
export const selectIsExportPanelOpen = (state) => state.export.isExportPanelOpen;
export const selectTotalActiveExports = (state) => state.export.totalActiveExports;

// Memoized selectors to prevent unnecessary rerenders
export const selectActiveExports = createSelector(
  [selectAllExports],
  (exports) => 
    Object.values(exports).filter(exportTask => 
      [EXPORT_STATUS.PENDING, EXPORT_STATUS.EXPORTING].includes(exportTask.status)
    )
);

export const selectCompletedExports = createSelector(
  [selectAllExports],
  (exports) => 
    Object.values(exports).filter(exportTask => 
      exportTask.status === EXPORT_STATUS.COMPLETED
    )
);

export default exportSlice.reducer;
